/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type FormEvent,
    type ReactElement,
    type ReactNode,
    useCallback,
    useRef,
    useState,
    useEffect,
    useMemo,
} from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { useRouteLoaderData } from 'react-router';
import type { loader as appLayoutLoader } from '@/routes/_app';
import { SearchPopupPageRegion } from '@/components/search/search-popup-page-region';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import debounce from 'lodash.debounce';
import { Input } from '@/components/ui/input';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Search as SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Suggestions from '@/components/search/suggestions';
import { useSearchSuggestions } from '@/hooks/use-search-suggestions';
import { useSearchSuggestionProductImages } from '@/hooks/use-search-suggestion-product-images';
import { useTransformSearchSuggestions } from '@/hooks/use-transform-search-suggestions';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { getSessionJSONItem, setSessionJSONItem, clearSessionJSONItem } from '@/lib/utils';
import { openShopperAgentAndSendMessage } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';

const RECENT_SEARCH_LIMIT = 5;
const RECENT_SEARCH_KEY = 'recent-search-key';
const RECENT_SEARCH_MIN_LENGTH = 3;

interface SearchBarProps {
    onSubmitSearch?: () => void;
    trailingAction?: ReactNode;
    /** When true (header search sheet), always show the suggestions panel so PD promo and recent searches are visible. */
    suggestionsPanelDefaultOpen?: boolean;
}

export default function SearchBar({
    onSubmitSearch,
    trailingAction,
    suggestionsPanelDefaultOpen = false,
}: SearchBarProps = {}): ReactElement {
    const { t } = useTranslation('header');
    const navigate = useNavigate();
    const appLayoutData = useRouteLoaderData<typeof appLayoutLoader>('routes/_app');
    const config = useConfig();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [query, setQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(() => {
        if (suggestionsPanelDefaultOpen) {
            return true;
        }
        return (getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || []).length > 0;
    });
    const queryRef = useRef(query);
    const refetchRef = useRef<() => Promise<void>>(() => Promise.resolve());

    const { data: suggestions, refetch } = useSearchSuggestions({
        q: query,
        expand: ['images', 'prices'],
        includeEinsteinSuggestedPhrases: true,
        enabled: query.trim().length >= RECENT_SEARCH_MIN_LENGTH,
    });

    const transformedSuggestions = useTransformSearchSuggestions(suggestions);
    const { imageByProductId, isHydrating: isHydratingSuggestionProductImages } =
        useSearchSuggestionProductImages(transformedSuggestions?.productSuggestions);

    const searchSuggestions = useMemo(() => {
        if (!transformedSuggestions || imageByProductId.size === 0) {
            return transformedSuggestions;
        }

        return {
            ...transformedSuggestions,
            productSuggestions: transformedSuggestions.productSuggestions.map((suggestion) => ({
                ...suggestion,
                image:
                    suggestion.image ||
                    (suggestion.productId ? imageByProductId.get(suggestion.productId) : undefined),
            })),
        };
    }, [transformedSuggestions, imageByProductId]);

    useEffect(() => {
        queryRef.current = query;
        refetchRef.current = refetch;
    }, [query, refetch]);

    const saveRecentSearch = useCallback((searchText: string) => {
        let searches = getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
        searches = searches.filter((savedSearchTerm) => {
            return searchText.toLowerCase() !== savedSearchTerm.toLowerCase();
        });
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT);
        setSessionJSONItem(RECENT_SEARCH_KEY, searches);
    }, []);

    const debouncedRefetch = useMemo(() => {
        return debounce(() => {
            const currentQuery = queryRef.current;
            if (currentQuery.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
                void refetchRef.current();
            }
        }, config.pages.search.suggestionsDebounce);
    }, [config.pages.search.suggestionsDebounce]);

    useEffect(() => {
        if (query.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedRefetch();
        } else {
            debouncedRefetch.cancel();
        }

        return () => {
            debouncedRefetch.cancel();
        };
    }, [query, debouncedRefetch]);

    const shouldOpenPopover = useCallback(() => {
        if (suggestionsPanelDefaultOpen) {
            setShowSuggestions(true);
            return;
        }

        const recentSearches = getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
        const searchSuggestionsAvailable =
            searchSuggestions &&
            (searchSuggestions.categorySuggestions.length > 0 ||
                searchSuggestions.productSuggestions.length > 0 ||
                (searchSuggestions.popularSearchSuggestions?.length ?? 0) > 0);

        if (recentSearches.length > 0 || (searchSuggestionsAvailable && inputRef.current?.value)) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    }, [searchSuggestions, suggestionsPanelDefaultOpen]);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setQuery(value);
            shouldOpenPopover();
        },
        [shouldOpenPopover]
    );

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            if (inputRef.current?.value?.trim()) {
                const searchQuery = inputRef.current.value.trim();
                saveRecentSearch(searchQuery);
                setShowSuggestions(false);
                onSubmitSearch?.();
                void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, {
                    state: { query: searchQuery },
                });
            }
        },
        [navigate, onSubmitSearch, saveRecentSearch]
    );

    const closeAndNavigate = useCallback(
        (link: string) => {
            inputRef.current?.blur();
            setShowSuggestions(false);
            setQuery('');
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            if (link) {
                onSubmitSearch?.();
                void navigate(link);
            }
        },
        [navigate, onSubmitSearch]
    );

    const clearRecentSearches = useCallback(() => {
        clearSessionJSONItem(RECENT_SEARCH_KEY);
        setShowSuggestions(false);
    }, []);

    const showShopperAgent =
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);

    const onShopperAgentClick = useCallback(() => {
        const searchText = inputRef.current?.value?.trim() ?? query.trim();
        setShowSuggestions(false);
        setQuery('');
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        openShopperAgentAndSendMessage(searchText);
    }, [query]);

    useEffect(() => {
        shouldOpenPopover();
    }, [query, suggestions, shouldOpenPopover]);

    return (
        <UITarget targetId="sfcc.header.search.input">
            <Popover open={showSuggestions}>
                <div className="m-4 border-b border-filter-selected p-4">
                    <div className="flex items-start gap-3">
                        <form onSubmit={handleSubmit} className="relative z-10 min-w-0 flex-1">
                            <div className="relative">
                                <PopoverTrigger asChild>
                                    <Input
                                        ref={inputRef}
                                        type="text"
                                        placeholder={t('searchPlaceholder')}
                                        className="h-14 w-full rounded-none border-0 bg-header-menu-icon-hover-background pl-10 shadow-none focus-visible:border-0 focus-visible:ring-0"
                                        onChange={handleInputChange}
                                        onFocus={shouldOpenPopover}
                                        aria-label={t('searchPlaceholder')}
                                        aria-autocomplete="list"
                                        aria-expanded={showSuggestions}
                                        aria-haspopup="listbox"
                                        role="combobox"
                                        data-testid="header-search"
                                    />
                                </PopoverTrigger>
                                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2" />
                            </div>
                        </form>
                        {trailingAction}
                    </div>
                </div>
                {showSuggestions && (
                    <div
                        className="w-full max-h-[min(70vh,32rem)] bg-background p-4 text-foreground"
                        role="listbox"
                        aria-label={t('searchSuggestions')}>
                        <Suggestions
                            searchSuggestions={searchSuggestions}
                            hydratingProductImages={isHydratingSuggestionProductImages}
                            recentSearches={getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || []}
                            closeAndNavigate={closeAndNavigate}
                            clearRecentSearches={clearRecentSearches}
                            showShopperAgent={showShopperAgent}
                            onShopperAgentClick={onShopperAgentClick}
                            searchPopupSlot={
                                <SearchPopupPageRegion
                                    page={appLayoutData?.searchPopupPage ?? undefined}
                                    className="mt-4"
                                    data-testid="search-popup-pd-region"
                                />
                            }
                        />
                    </div>
                )}
            </Popover>
        </UITarget>
    );
}
